import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { collection, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import {
  FaTimes, FaArrowLeft, FaArrowRight, FaPaperPlane, FaFileUpload, FaFileAlt, FaCheckCircle, FaCheck
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { db, storage } from '../../firebase/config';
import Captcha from '../../components/Captcha/Captcha';
import {
  LANGUAGE_LEVELS, CV_MAX_BYTES, CV_TYPES, answerTypeOf, questionText, meetsIdeal,
  isLinkedInProfileUrl, jobTitle
} from './careersOptions';

const MIN_FORM_TIME_MS = 3000;
const AUTO_ADVANCE_MS = 350;
const EMPTY = { name: '', email: '', phone: '', linkedinUrl: '', position: '', message: '', website: '' };

/*
 * Full-screen, one-step-at-a-time application form (Typeform style).
 * Steps: contact → [position, spontaneous only] → CV → one screen per screening question → message + send.
 */
const ApplicationWizard = ({ job = null, onClose }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'fr';
  const questions = useMemo(() => job?.screeningQuestions || [], [job]);

  const steps = useMemo(() => [
    { key: 'contact' },
    ...(job ? [] : [{ key: 'position' }]),
    { key: 'cv' },
    ...questions.map((q, i) => ({ key: `q-${q.id}`, question: q, number: i + 1 })),
    { key: 'final' }
  ], [job, questions]);

  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [answers, setAnswers] = useState({});
  const [cv, setCv] = useState(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const openedAt = useRef(Date.now());
  const captchaRef = useRef(null);
  const fileRef = useRef(null);
  const stepRef = useRef(null);
  const advanceTimer = useRef(null);

  const step = steps[index];
  const isLast = index === steps.length - 1;
  const dirty = Object.values(form).some(Boolean) || cv || Object.keys(answers).length > 0;

  // Lock page scroll while the form is open
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
      clearTimeout(advanceTimer.current);
    };
  }, []);

  // Focus the first field of each step
  useEffect(() => {
    const timer = setTimeout(() => {
      // Choice steps focus the step itself, so letter shortcuts work without pre-selecting an option
      const target = stepRef.current?.querySelector('[data-autofocus]') || stepRef.current;
      if (target) target.focus();
    }, 320);
    return () => clearTimeout(timer);
  }, [index, sent]);

  const requestClose = () => {
    if (!sent && dirty && !window.confirm(t('careers.wizard.confirmClose'))) return;
    onClose();
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const validateStep = () => {
    if (step.key === 'contact') {
      if (!form.name.trim() || !form.email.trim()) return 'careers.form.error';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'contact.form.emailError';
      if (form.linkedinUrl.trim() && !isLinkedInProfileUrl(form.linkedinUrl)) return 'careers.form.linkedinError';
    }
    if (step.key === 'cv' && !cv) return 'careers.form.cvMissing';
    if (step.question) {
      const value = answers[step.question.id];
      if (value === undefined || value === '') return 'careers.wizard.answerMissing';
      if (answerTypeOf(step.question) === 'number' && !(Number(value) >= 0)) return 'careers.wizard.numberInvalid';
    }
    return null;
  };

  const goTo = (target) => {
    clearTimeout(advanceTimer.current);
    setError('');
    setDirection(target > index ? 1 : -1);
    setIndex(target);
  };

  const next = () => {
    const problem = validateStep();
    if (problem) { setError(t(problem)); return; }
    if (!isLast) goTo(index + 1);
  };

  const back = () => { if (index > 0) goTo(index - 1); };

  // Single-choice answers move on by themselves, like Typeform
  const choose = (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
    setError('');
    clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => {
      setDirection(1);
      setIndex(i => Math.min(i + 1, steps.length - 1));
    }, AUTO_ADVANCE_MS);
  };

  const pickFile = (file) => {
    setError('');
    if (!file) return;
    if (!CV_TYPES.includes(file.type)) setError(t('careers.form.cvTypeError'));
    else if (file.size > CV_MAX_BYTES) setError(t('careers.form.cvSizeError'));
    else setCv(file);
  };

  const submit = async () => {
    setError('');
    // Honeypot: silently drop — don't tell the bot it failed.
    if (form.website) { setSent(true); return; }
    if (Date.now() - openedAt.current < MIN_FORM_TIME_MS) { setError(t('contact.form.botError')); return; }
    if (!captchaRef.current || !captchaRef.current.verify()) {
      if (captchaRef.current) captchaRef.current.refresh();
      setError(t('contact.form.captchaError'));
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload the CV under the application's id, then save the application
      const appRef = doc(collection(db, 'applications'));
      const safeName = cv.name.replace(/[^\w.-]+/g, '_').slice(-80);
      const cvPath = `cvs/${appRef.id}/${safeName}`;
      await uploadBytes(storageRef(storage, cvPath), cv, { contentType: cv.type });

      const answerList = questions.map(q => {
        const raw = answers[q.id];
        const answer = answerTypeOf(q) === 'number' ? Number(raw) : raw;
        return {
          questionId: q.id,
          category: q.category,
          question: questionText(q, job.language || 'fr', job),
          answer,
          idealAnswer: q.idealAnswer,
          answerType: answerTypeOf(q),
          required: !!q.required,
          meets: meetsIdeal(q, answer)
        };
      });

      await setDoc(appRef, {
        type: job ? 'job' : 'spontaneous',
        jobId: job ? job.id : null,
        jobTitle: job ? jobTitle(job) : form.position.trim(),
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        linkedinUrl: form.linkedinUrl.trim(),
        message: form.message.trim(),
        cvPath,
        cvName: cv.name.slice(-120),
        answers: answerList,
        knockout: answerList.some(a => a.required && !a.meets),
        language: lang,
        status: 'new',
        createdAt: serverTimestamp()
      });
      setSent(true);
    } catch (err) {
      console.error('Error sending application:', err);
      setError(t('contact.form.sendError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enter moves forward (Shift+Enter keeps new lines in the message)
  const onFormKeyDown = (e) => {
    // Letter shortcuts (A, B, C…) pick an option on choice questions
    if (step.question && answerTypeOf(step.question) !== 'number' && /^[a-z]$/i.test(e.key) && !e.ctrlKey && !e.metaKey) {
      const values = answerTypeOf(step.question) === 'level' ? LANGUAGE_LEVELS.map(l => l.value) : ['yes', 'no'];
      const value = values[e.key.toUpperCase().charCodeAt(0) - 65];
      if (value) { e.preventDefault(); choose(step.question.id, value); }
      return;
    }
    if (e.key !== 'Enter' || e.shiftKey || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON') return;
    e.preventDefault();
    if (isLast) submit(); else next();
  };

  const progress = sent ? 100 : Math.round((index / steps.length) * 100);
  const heading = job ? jobTitle(job) : t('careers.spontaneous.title');

  const choiceButtons = (q, options) => (
    <div className="wz-choices" role="radiogroup">
      {options.map((option, i) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={answers[q.id] === option.value}
          className={`wz-choice ${answers[q.id] === option.value ? 'selected' : ''}`}
          onClick={() => choose(q.id, option.value)}
        >
          <span className="wz-key">{String.fromCharCode(65 + i)}</span>
          {option.label}
          {answers[q.id] === option.value && <FaCheck className="wz-check" />}
        </button>
      ))}
    </div>
  );

  const renderStep = () => {
    if (step.key === 'contact') {
      return (
        <>
          <h2>{t('careers.wizard.contactTitle')}</h2>
          <p className="wz-hint">{t('careers.wizard.contactHint')}</p>
          <div className="wz-grid">
            <label className="wz-field">
              <span>{t('contact.form.name')} *</span>
              <input name="name" value={form.name} onChange={handleChange} autoComplete="name" data-autofocus />
            </label>
            <label className="wz-field">
              <span>{t('contact.form.email')} *</span>
              <input name="email" type="email" value={form.email} onChange={handleChange} autoComplete="email" />
            </label>
            <label className="wz-field">
              <span>{t('careers.form.phone')}</span>
              <input name="phone" type="tel" value={form.phone} onChange={handleChange} autoComplete="tel" />
            </label>
            <label className="wz-field">
              <span>{t('careers.form.linkedin')}</span>
              <input name="linkedinUrl" type="url" value={form.linkedinUrl} onChange={handleChange} placeholder="https://www.linkedin.com/in/…" />
            </label>
          </div>
        </>
      );
    }

    if (step.key === 'position') {
      return (
        <>
          <h2>{t('careers.wizard.positionTitle')}</h2>
          <p className="wz-hint">{t('careers.wizard.positionHint')}</p>
          <input className="wz-big-input" name="position" value={form.position} onChange={handleChange}
            placeholder={t('careers.form.positionPlaceholder')} data-autofocus />
        </>
      );
    }

    if (step.key === 'cv') {
      return (
        <>
          <h2>{t('careers.wizard.cvTitle')}</h2>
          <p className="wz-hint">{t('careers.form.cvHint')}</p>
          {cv ? (
            <div className="wz-file">
              <FaFileAlt /> <span>{cv.name}</span>
              <button type="button" onClick={() => setCv(null)} aria-label={t('careers.form.cvRemove')}><FaTimes /></button>
            </div>
          ) : (
            <button
              type="button"
              className={`wz-drop ${dragOver ? 'over' : ''}`}
              onClick={() => fileRef.current.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files[0]); }}
              data-autofocus
            >
              <FaFileUpload />
              <strong>{t('careers.form.cvChoose')}</strong>
              <small>{t('careers.wizard.dropHint')}</small>
            </button>
          )}
          <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" hidden
            onChange={(e) => { pickFile(e.target.files[0]); e.target.value = ''; }} />
        </>
      );
    }

    if (step.question) {
      const q = step.question;
      const type = answerTypeOf(q);
      return (
        <>
          <p className="wz-eyebrow">{t('careers.wizard.questionCount', { n: step.number, total: questions.length })}</p>
          <h2>{questionText(q, lang, job)}</h2>
          {type === 'yesno' && choiceButtons(q, [
            { value: 'yes', label: t('careers.form.yes') },
            { value: 'no', label: t('careers.form.no') }
          ])}
          {type === 'level' && choiceButtons(q, LANGUAGE_LEVELS.map(l => ({ value: l.value, label: l[lang] })))}
          {type === 'number' && (
            <div className="wz-number">
              <input
                className="wz-big-input"
                type="number"
                min="0"
                max="60"
                inputMode="numeric"
                value={answers[q.id] ?? ''}
                onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                placeholder="0"
                data-autofocus
              />
              <span>{t('careers.wizard.years')}</span>
            </div>
          )}
        </>
      );
    }

    return (
      <>
        <h2>{t('careers.wizard.finalTitle')}</h2>
        <p className="wz-hint">{t('careers.wizard.finalHint')}</p>
        <label className="wz-field">
          <span>{t('careers.form.message')}</span>
          <textarea name="message" rows="4" value={form.message} onChange={handleChange}
            placeholder={t('careers.form.messagePlaceholder')} data-autofocus />
        </label>
        {/* Honeypot — hidden from users via CSS and from screen readers via aria-hidden */}
        <div className="cf-hp" aria-hidden="true">
          <label htmlFor="wz-website">Website</label>
          <input id="wz-website" name="website" value={form.website} onChange={handleChange} tabIndex={-1} autoComplete="off" />
        </div>
        <Captcha ref={captchaRef} label={t('contact.form.captchaLabel')} placeholder={t('contact.form.captchaPlaceholder')} />
        <p className="wz-privacy">
          {t('careers.form.privacyNote')}{' '}
          <Link to="/privacy-policy" target="_blank">{t('contact.form.privacyLink')}</Link>
        </p>
      </>
    );
  };

  return (
    <div className="wizard" role="dialog" aria-modal="true" aria-labelledby="wizard-title">
      <header className="wz-header">
        <div className="wz-header-inner">
          <p id="wizard-title" className="wz-title">
            <span>{job ? t('careers.wizard.applyingFor') : ''}</span> {heading}
          </p>
          <button type="button" className="wz-close" onClick={requestClose} aria-label={t('careers.wizard.close')}>
            <FaTimes />
          </button>
        </div>
        <div className="wz-progress" role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
          <div className="wz-progress-bar" style={{ width: `${progress}%` }} />
        </div>
        {!sent && <p className="wz-step-count">{t('careers.wizard.step', { current: index + 1, total: steps.length })}</p>}
      </header>

      <div className="wz-body" onKeyDown={onFormKeyDown}>
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          <motion.div
            key={sent ? 'sent' : step.key}
            ref={stepRef}
            tabIndex={-1}
            className="wz-step"
            custom={direction}
            initial={{ opacity: 0, y: direction * 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: direction * -40 }}
            transition={{ duration: 0.25 }}
          >
            {sent ? (
              <div className="wz-sent">
                <FaCheckCircle className="wz-sent-icon" />
                <h2>{t('careers.form.sentTitle')}</h2>
                <p className="wz-hint">{t('careers.form.success')}</p>
                <button type="button" className="btn btn-primary" onClick={onClose} data-autofocus>
                  {t('careers.wizard.close')}
                </button>
              </div>
            ) : (
              <>
                {renderStep()}
                {error && <p className="wz-error" role="alert">{error}</p>}
                <div className="wz-actions">
                  {isLast ? (
                    <button type="button" className="btn btn-primary wz-next" onClick={submit} disabled={isSubmitting}>
                      <FaPaperPlane /> {isSubmitting ? t('contact.form.sending') : t('careers.form.submit')}
                    </button>
                  ) : (
                    <button type="button" className="btn btn-primary wz-next" onClick={next}>
                      {t('careers.wizard.next')} <FaArrowRight />
                    </button>
                  )}
                  {!isLast && step.key !== 'cv' && !step.question && (
                    <span className="wz-enter">{t('careers.wizard.enterHint')}</span>
                  )}
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {!sent && (
        <footer className="wz-footer">
          <button type="button" className="wz-back" onClick={back} disabled={index === 0}>
            <FaArrowLeft /> {t('careers.wizard.back')}
          </button>
        </footer>
      )}
    </div>
  );
};

export default ApplicationWizard;
